import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import type {PanelContentProps, PanelFooterProps, PanelHeaderProps} from '@Components/panel';
import {Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelFooterSecondaryCustomAction, PanelHeader} from '@Components/panel';
import {isMobile} from 'react-device-detect';
import {shouldAiPanelCloseOnItemAdd} from '@Panels/stock-panel/stock-panel.library';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import {copyToClipboard} from '@Utils/clipboard.util';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import type {PanelFooterSecondaryActionType} from '@Components/panel/components/panel-footer-secondary-actions/panel-footer-secondary-actions';
import type {AiVideoPanelProps} from '@Panels/ai-video-panel/ai-video-panel.types';
import {AI_VIDEO_FOOTER_OPTIONS_PANEL_ID, DEFAULT_AI_VIDEO_PANEL_ID} from '@Panels/ai-video-panel/ai-video-panel.types';
import {AiVideoView} from '@Panels/ai-video-panel/components/ai-video-view/ai-video-view';
import {confirmAndDeleteGeneratedAiVideo, getGeneratedVideoMenuItems, uploadGeneratedAiVideo} from '@Libraries/ai-video.library';
import type {AiVideoBackendData} from '@Libraries/ai-video.library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {openErrorModal} from '@Modals/error-modal';
import {AI_VIDEO_GENERATION_LOADING_KEY, getLoadingKeyForAiVideoAddSelected} from '@Panels/ai-video-panel/ai-video-panel.library';
import {removeAiVideoGenerationItem, selectFailedAiVideos, selectGeneratingAiVideoCount} from '@Components/poster-editor/poster-editor-reducer';
import {getSelectedGridItems} from '@Components/base-grid/base-grid-reducer';
import {GENERATED_VIDEO_GRID_ID} from '@Panels/ai-video-panel/components/generated-video-grid/generated-video-grid.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './ai-video-panel.module.scss';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import type {CanvasDropPosition} from '@Libraries/canvas-item-drag.library';
import {placeItemAtDropPosition} from '@Libraries/canvas-item-drag.library';
import {AiVideoGenerationLimitReached} from '@Panels/ai-video-panel/components/ai-video-generation-limit-reached';

export default function AiVideoPanel({isOpenedInPopOver = false, showBackButton = true, isFullScreen = false, enableMultiSelection = false, maxSelectionNumber, ...props}: AiVideoPanelProps): ReactElement {
  const panelId = useRef(props.panelId ?? DEFAULT_AI_VIDEO_PANEL_ID);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const {topShadow} = useTopBottomScrollShadow(gridRef, !!gridRef.current);
  const dispatch = useAppDispatch();
  const selectedItems = useAppSelector((state) => getSelectedGridItems(state, GENERATED_VIDEO_GRID_ID));
  const fullscreenPreviewOpenerRef = useRef<((video: AiVideoBackendData) => void) | null>(null);
  const closeFooterOptionsModal = useDefaultPanelClose(AI_VIDEO_FOOTER_OPTIONS_PANEL_ID);
  const loadingSkeletonsCount = useAppSelector(selectGeneratingAiVideoCount);
  const loadingSkeletonsCountRef = useRef(loadingSkeletonsCount);
  loadingSkeletonsCountRef.current = loadingSkeletonsCount;
  const failedVideos = useAppSelector(selectFailedAiVideos);
  const failedVideosRef = useRef(failedVideos);
  failedVideosRef.current = failedVideos;

  useEffect(() => {
    return (): void => {
      if (loadingSkeletonsCountRef.current > 0) {
        showLoading(AI_VIDEO_GENERATION_LOADING_KEY, {
          text: `${window.i18next.t('pmwjs_generating')} ${loadingSkeletonsCountRef.current} ${loadingSkeletonsCountRef.current > 1 ? window.i18next.t('pmwjs_ai_videos') : window.i18next.t('pmwjs_ai_video')}`,
        });
      }
      failedVideosRef.current.forEach((item) => {
        dispatch(removeAiVideoGenerationItem(item.id));
      });
    };
  }, []);

  const addVideoIdsToDesign = async (videoIds: number[], dropPosition?: CanvasDropPosition): Promise<void> => {
    const loadingKey = getLoadingKeyForAiVideoAddSelected(panelId.current);
    showLoading(loadingKey, {text: videoIds.length > 1 ? window.i18next.t('pmwjs_adding') : window.i18next.t('pmwjs_adding_1_of_1')});
    try {
      const uploaded = await Promise.all(videoIds.map((id) => uploadGeneratedAiVideo(id)));
      await props.onAddSelected(uploaded.map((video) => placeItemAtDropPosition(video, dropPosition)));
    } catch (e) {
      console.error(e);
      openErrorModal({message: window.i18next.t('pmwjs_internet_error_message'), log: false});
    } finally {
      hideLoading(loadingKey);
    }
  };

  const replaceWithGeneratedVideo = (videos: AiVideoBackendData[]): void => {
    if (!props.shouldReplaceOnGenerate || videos.length === 0) {
      return;
    }
    props.onClose?.();
    void addVideoIdsToDesign(videos.map((video) => video.id));
  };

  const onClose = (): void => {
    props.onClose?.();
  };

  const onBack = (e?: unknown): void => {
    if (props.onBack) {
      props.onBack(e);
    }
  };

  const shouldShowHeader = (): boolean => {
    if (isMobile || isFullScreen) {
      return true;
    }
    return !!props.onBack;
  };

  const shouldShowHeaderBorder = (): boolean => {
    if (isFullScreen) {
      return true;
    }
    return topShadow;
  };

  const onAddSelected = (): void => {
    if (selectedItems.length === 0) {
      return;
    }
    const videoIds = selectedItems.map((item) => Number(item.id)).filter((id) => Number.isFinite(id));
    if (videoIds.length === 0) {
      return;
    }
    if (isFullScreen || enableMultiSelection || shouldAiPanelCloseOnItemAdd()) {
      props.onClose?.();
    }
    void addVideoIdsToDesign(videoIds);
  };

  const getPanelHeader = (): ReactElement<PanelHeaderProps> | null => {
    if (shouldShowHeader()) {
      return (
        <PanelHeader
          backButtonIconSize={isOpenedInPopOver ? IconSize.SIZE_ICON_16 : undefined}
          titleClasses={`_fit-width content-ai ${isFullScreen ? 'body-l-bold' : 'body-md-bold'}`}
          leftIconProps={isFullScreen ? {icon: 'icon-video-ai', size: IconSize.SIZE_ICON_20, className: `content-ai ${showBackButton ? '' : 'spacing-m-l-10'}`} : undefined}
          className={`${shouldShowHeaderBorder() ? '' : '_borderless'} ${isOpenedInPopOver ? 'spacing-p-l-4 spacing-p-r-4' : ''}`}
          title={window.i18next.t('pmwjs_ai_videos')}
          showCloseButton={isFullScreen || isMobile}
          onClose={onClose}
          showBackButton={showBackButton}
          onBackButton={onBack}
          forceCenterAlignTitleWithActionItems
          isOpenedInModal={isMobile}
        />
      );
    }
    return null;
  };

  const getSelectedVideoBackendData = (): AiVideoBackendData | null => {
    if (selectedItems.length !== 1) {
      return null;
    }
    const itemData = selectedItems[0].data as {video?: AiVideoBackendData} | undefined;
    return itemData?.video ?? null;
  };

  const getFooterOptionsMenuItems = (video: AiVideoBackendData): ControlledListItemType[] => {
    return getGeneratedVideoMenuItems({
      onFullscreenPreview: (): void => {
        closeFooterOptionsModal();
        fullscreenPreviewOpenerRef.current?.(video);
      },
      onCopyPrompt: (): void => {
        closeFooterOptionsModal();
        void copyToClipboard(video.prompt.text);
        showMessageGrowl({text: window.i18next.t('pmwjs_prompt_copied'), type: GROWL_TYPE.SUCCESS});
      },
      onDelete: (): void => {
        closeFooterOptionsModal();
        confirmAndDeleteGeneratedAiVideo(video, GENERATED_VIDEO_GRID_ID, GENERATED_VIDEO_GRID_ID);
      },
    });
  };

  const openFooterOptionsModal = (video: AiVideoBackendData): void => {
    openMoreOptionsModal({
      panelId: AI_VIDEO_FOOTER_OPTIONS_PANEL_ID,
      headerTitle: window.i18next.t('pmwjs_options'),
      isSmallScreenModalBottomSheet: true,
      shouldCenterAlignTitle: true,
      content: <ControlledList className="spacing-m-0 spacing-p-4" items={getFooterOptionsMenuItems(video)} />,
    });
  };

  const getFooterSecondaryActions = (video: AiVideoBackendData): PanelFooterSecondaryActionType[] => {
    return [
      <PanelFooterSecondaryCustomAction key="btn-generated-video-options">
        <div className="flex-center">
          <Icon
            icon="icon-menu-dots"
            size={IconSize.SIZE_ICON_20}
            shape={IconShape.SQUARE}
            type={IconType.GHOST}
            isUsedAsButton
            ariaLabel={window.i18next.t('pmwjs_options')}
            onClick={(): void => {
              openFooterOptionsModal(video);
            }}
          />
        </div>
      </PanelFooterSecondaryCustomAction>,
    ];
  };

  const getPanelFooter = (): ReactElement<PanelFooterProps> | null => {
    if (selectedItems.length === 0) {
      return null;
    }

    const selectedVideoBackendData = isMobile ? getSelectedVideoBackendData() : null;
    if (selectedVideoBackendData) {
      return (
        <PanelFooter
          primaryActions={[
            <PanelFooterPrimaryDefaultAction key="btn-add-selected-ai-video" className="_full-width" text={window.i18next.t('pmwjs_add_selected')} onClick={onAddSelected} />,
          ]}
          primaryActionsClassName="flex-1"
          secondaryActions={getFooterSecondaryActions(selectedVideoBackendData)}
        />
      );
    }

    return (
      <PanelFooter
        fullWidthPrimaryActions={isMobile}
        primaryActions={[<PanelFooterPrimaryDefaultAction key="btn-add-selected-ai-video" text={window.i18next.t('pmwjs_add_selected')} onClick={onAddSelected} />]}
      />
    );
  };

  const getPanelContent = (): ReactElement<PanelContentProps> => {
    return (
      <PanelContent
        className={`${styles.scrollableContent} spacing-p-b-4 ${isFullScreen && !isMobile ? 'spacing-p-r-12 spacing-p-l-12 spacing-p-t-6 _position-relative' : 'spacing-p-r-4 spacing-p-l-4'}`}
        ref={gridRef}
      >
        {isFullScreen && !isMobile ? <AiVideoGenerationLimitReached isFullScreen={isFullScreen} /> : null}
        <AiVideoView
          onRegisterFullscreenPreviewOpener={(openPreview): void => {
            fullscreenPreviewOpenerRef.current = openPreview;
          }}
          onAddToDesign={(backendData, dropPosition): void => {
            if (isFullScreen || isMobile) {
              props.onClose?.();
            }
            void addVideoIdsToDesign([backendData.id], dropPosition);
          }}
          enableDragToCanvas={props.enableDragToCanvas}
          isFullScreen={isFullScreen}
          enableMultiSelection={enableMultiSelection}
          maxSelectionNumber={maxSelectionNumber}
          onVideoGenerated={replaceWithGeneratedVideo}
        />
      </PanelContent>
    );
  };

  return (
    <Panel
      panelId={panelId.current}
      className="ct-neutral-primary"
      key={panelId.current}
      panelHeader={getPanelHeader()}
      panelContent={getPanelContent()}
      panelFooter={getPanelFooter()}
    />
  );
}
